/** /pre_trip_items/ — cross-tenant pre-trip ITEM catalog.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Items have no `updated_ts` — writes are last-writer-wins
 * (no optimistic-concurrency token). An item is displayed as EXACTLY ONE of:
 *   - link  → `url` + `deep_link` (Open in Safari vs In-App Browser)
 *   - file  → `attachment` (a multipart file part)
 *   - text  → `text` (HTML)
 * `display_mode` reports the current mode. On write the caller nulls the
 * inactive fields; `attachment: null` drops an existing file, and a multipart
 * `attachment` part replaces it. The attachment renders directly from its plain
 * `url` (no image-base composition).
 */
import { apiDelete, apiGet, client } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { RenderingType } from './preTripItemTypes';
import type { TenantIdsParam } from '@/scope/tenantIds';

export type DisplayMode = 'link' | 'file' | 'text';

/** The item's category, brief form. `rendering_type` gates the editor's text
 *  mode (hidden for `2` = List with checkboxes). */
export interface ItemTypeBriefRef {
  id: number;
  name: string;
  rendering_type: RenderingType;
}

export interface AttachmentRef {
  id: number;
  name: string;
  /** Plain public S3 URL. */
  url: string | null;
  size: number;
}

export interface PreTripItemListItem {
  id: number;
  /** Owning tenant; null for unassigned/orphaned items (`Pre_Trip_Item.tour_operator`
   *  is nullable). The list grid renders null as '—'. */
  tenant: TenantRef | null;
  name: string;
  item_type: ItemTypeBriefRef | null;
  display_mode: DisplayMode;
  has_attachment: boolean;
  /** Internal note (hidden by default). */
  note: string | null;
  /** Link target + its open mode (hidden by default; only meaningful for
   *  `display_mode === 'link'`). */
  url: string | null;
  deep_link: boolean;
}

export interface PreTripItemDetail extends PreTripItemListItem {
  /** HTML. */
  text: string | null;
  attachment: AttachmentRef | null;
  atag: NamedRef[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  item_type?: number;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.item_type !== undefined) usp.set('item_type', String(params.item_type));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<PreTripItemListItem>> {
  return apiGet<PaginatedResponse<PreTripItemListItem>>(`/pre_trip_items/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<PreTripItemDetail> {
  return apiGet<PreTripItemDetail>(`/pre_trip_items/${id}/`);
}

/** The `_json` metadata of an item write (also the whole body of a no-file
 *  PATCH). `tour_operator` + `item_type` + `name` are required on create; the
 *  mode-resolution logic nulls the inactive fields. */
export interface PreTripItemWriteJson {
  tour_operator?: number;
  /** Category id. */
  item_type?: number;
  name?: string;
  note?: string | null;
  text?: string | null;
  url?: string | null;
  deep_link?: boolean;
  /** null removes the existing attachment; a multipart file part replaces it. */
  attachment?: null;
  atag?: { id: number }[];
}

/** POST /pre_trip_items/ — multipart (`_json` + `attachment` file) when a File
 *  is staged (file mode with a new file), else plain JSON (link / text). */
export async function create(body: PreTripItemWriteJson, attachment?: File | null): Promise<PreTripItemDetail> {
  if (attachment) {
    const fd = new FormData();
    fd.append('_json', JSON.stringify(body));
    fd.append('attachment', attachment);
    const r = await client.post<PreTripItemDetail>('/pre_trip_items/', fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  const r = await client.post<PreTripItemDetail>('/pre_trip_items/', body);
  return r.data;
}

/** PATCH /pre_trip_items/{id}/ — multipart when a File is staged (swap the
 *  file); plain JSON otherwise. To drop an existing file, put `attachment: null`
 *  in `body` and pass no File. */
export async function update(
  id: number,
  body: PreTripItemWriteJson,
  attachment?: File | null,
): Promise<PreTripItemDetail> {
  if (attachment) {
    const fd = new FormData();
    fd.append('_json', JSON.stringify(body));
    fd.append('attachment', attachment);
    const r = await client.patch<PreTripItemDetail>(`/pre_trip_items/${id}/`, fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  const r = await client.patch<PreTripItemDetail>(`/pre_trip_items/${id}/`, body);
  return r.data;
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/pre_trip_items/${id}/`);
}
